import { Injectable } from '@angular/core';
import { PublicClientApplication, AuthenticationResult, Configuration, LogLevel } from '@azure/msal-browser';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AzureAuthService {
  private msalInstance: PublicClientApplication;
  private initialized = false;

  // Define los scopes como constante de clase para reutilizarlos
  private readonly loginScopes = ['openid', 'profile', 'email', 'user.read'];
  private readonly apiScopes = ['api://ccf8ca36-0eb0-407f-ada4-24f6680ea9be/access_as_user'];
  // Scope de la API REST de Power BI (embed user-owns-data). Se pre-consiente en el login
  // (ver extraScopesToConsent) para que getPowerBiToken lo obtenga silencioso, sin popup.
  private readonly powerBiScopes = ['https://analysis.windows.net/powerbi/api/Report.Read.All'];
  constructor() {
    const msalConfig: Configuration = {
      auth: {
        clientId: 'ccf8ca36-0eb0-407f-ada4-24f6680ea9be', // Tu clientId
        authority: 'https://login.microsoftonline.com/0088769c-6ecf-41ee-a374-2f0f5c0aa8cb/v2.0',
        redirectUri: environment.redirectUriAzureLogin
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: false
      },
      system: {
        loggerOptions: {
          loggerCallback: (level, message) => {
            // // console.log(message);
          },
          logLevel: LogLevel.Info,
          piiLoggingEnabled: false
        }
      }
    };

    this.msalInstance = new PublicClientApplication(msalConfig);
  }

  async init(): Promise<void> {
    if (!this.initialized) {
      await this.msalInstance.initialize();
      this.initialized = true;
    }
  }

  async login(): Promise<void> {
    await this.init();
    await this.msalInstance.loginRedirect({
      scopes: this.loginScopes,
      // Pre-consiente Power BI en el login (gesto del usuario → redirect, nunca bloqueado).
      // Así el tablero financiero obtiene su token con acquireTokenSilent en prod, sin popup.
      extraScopesToConsent: this.powerBiScopes
    });
  }

  async logout(): Promise<void> {
    await this.init();
    await this.msalInstance.logoutRedirect();
  }

  async getAccount() {
    await this.init();
    const accounts = this.msalInstance.getAllAccounts();
    return accounts.length > 0 ? accounts[0] : null;
  }

  async handleRedirect(): Promise<AuthenticationResult | null> {
    await this.init();
    return this.msalInstance.handleRedirectPromise();
  }

  // Método para adquirir token silenciosamente (útil para llamadas API)
  async getTokenSilent() {
    await this.init();
    const account = await this.getAccount();

    if (account) {
      try {
        return await this.msalInstance.acquireTokenSilent({
          scopes: this.loginScopes,
          account
        });
      } catch (error) {
        console.error('Error acquiring token silently:', error);
        return null;
      }
    }
    return null;
  }

  /**
   * Obtiene el access token real para enviar al backend.
   * Retorna el string JWT o null si falla.
   */
  async getAccessToken(): Promise<string | null> {
    await this.init();
    const account = await this.getAccount();

    if (account) {
      try {
        const result = await this.msalInstance.acquireTokenSilent({
          scopes: this.apiScopes,
          account
        });
        return result.accessToken;
      } catch (error) {
        console.error('Error acquiring access token:', error);
        return null;
      }
    }
    return null;
  }

  /**
   * Token de acceso para la API REST de Power BI (embed user-owns-data).
   * Requiere que la app Azure tenga el permiso delegado Power BI Service → Report.Read.All
   * (con admin consent). Intenta silencioso y cae a popup si hace falta consentimiento.
   */
  async getPowerBiToken(): Promise<string | null> {
    await this.init();
    const scopes = this.powerBiScopes;
    const account = await this.getAccount();
    try {
      if (account) {
        const result = await this.msalInstance.acquireTokenSilent({ scopes, account });
        return result.accessToken;
      }
    } catch (error) {
      console.warn('PBI token silencioso falló, intento popup:', error);
    }
    try {
      const result = await this.msalInstance.acquireTokenPopup({ scopes });
      return result.accessToken;
    } catch (error) {
      console.error('No se pudo obtener token de Power BI:', error);
      return null;
    }
  }

  deleteInfo() {
    this.msalInstance.clearCache();
  }
}
